<!--
An input on which all vee validation rules can be applied throught the "rules" prop.
See vee-validation-setup.ts for list of available rules

Example use : 
 <InputWithValidation
    rules="required|email"
    type="email"
    :tooltip="$t('input-email-tooltip')"
    :label="$t('input-email')"
    placeholder="e.g. EXAMPLE VALUE"
    v-model="email"
  />
-->
<template>
  <!-- Nous utilisons toujours ValidationProvider, mais c'est maintenant notre wrapper autour du Field de vee-validate v4 -->
  <ValidationProvider
    v-slot="{ errors, valid, handleChange, handleBlur }"
    :vid="(group != '' ? group + '_' : '') + vid"
    :name="($attrs.name as string) || ($attrs.label as string)"
    :rules="rulesWithoutRequiredRulesIfValidationDisabled"
    :model-value="innerValue"
    @update:model-value="updateValue"
  >
    <b-field
      :type="{
        'is-normal': disabled,
        'is-danger': showValidationErrors && (errors[0] || customError.length > 0),
        'is-success': showValidationErrors && valid,
      }"
      :message="showValidationErrors ? [errors + customError] : ''"
      class="InputWithValidation-field"
    >
      <template v-if="!hiddenLabel" #label>
        <FormInputLabel
          :validation-activated="validationActivated"
          :show-validation-errors="showValidationErrors"
          :label="($attrs.label as string) || ''"
          :is-mandatory="isMandatory"
          :disabled="disabled"
          :input-notice="inputNotice"
          :tooltip="tooltip"
        ></FormInputLabel>
      </template>

      <div :class="`InputWithValidation-control-container ${controlRight ? 'control-right' : ''}`">
        <p v-if="control" class="InputWithValidation-control">
          <span class="button is-static">{{ control }}</span>
        </p>
        <p v-if="controlAction" class="InputWithValidation-control">
          <slot></slot>
        </p>
        <b-input
          ref="inputField"
          v-model="innerValue"
          :placeholder="placeholder"
          :type="($attrs.type as string) || ''"
          :password-reveal="passwordReveal"
          :maxlength="maxlength"
          :disabled="disabled"
          :custom-class="disabled ? 'InputWithValidation-disabled' : 'auto-expand'"
          :autocomplete="autocomplete"
          @update:model-value="
            (val: any) => {
              handleChange && handleChange(val)
              adjustTextAreaHeight()
              // Émettre l'état de validation
              $emit('validation-changed', { valid, errors })
              // Signaler dès la première frappe que le formulaire est dirty
              notifyInputDirty()
            }
          "
          @blur="(event: any) => onBlur(event, handleBlur)"
        ></b-input>
      </div>
    </b-field>
  </ValidationProvider>
</template>

<script lang="ts">
import { defineComponent } from "vue"
import { debounce } from "lodash"
// Importation du ValidationProvider depuis notre service (wrapper v4)
import ValidationProvider from "@/services/validation/ValidationProvider.vue"
import FormInputLabel from "./FormInputLabel.vue"
import { BInput, BField } from "buefy"
import { filterRulesByValidationStatus } from "@/services/validation/validation-rules-utils"
import { eventBus } from "@/services/EventBus"

export default defineComponent({
  name: "InputWithValidation",
  components: {
    ValidationProvider,
    FormInputLabel,
    BInput,
    BField,
  },
  props: {
    group: {
      type: String,
      default: "",
    },
    vid: {
      type: String,
      required: false,
      default: "",
    },
    rules: {
      type: [String, Array],
      default: "",
      required: false,
    },
    modelValue: {
      type: [String, Number],
      default: "",
      required: false,
    },
    tooltip: {
      type: String,
      default: "",
      required: false,
    },
    inputNotice: {
      type: String,
      default: "",
      required: false,
    },
    control: {
      type: String,
      default: "",
      required: false,
    },
    placeholder: {
      type: String,
      default: "",
    },
    customError: {
      type: String,
      default: "",
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    hiddenLabel: {
      type: Boolean,
      default: false,
    },
    passwordReveal: {
      type: Boolean,
      default: false,
    },
    maxlength: {
      type: [String, Number],
      default: "",
    },
    controlRight: {
      type: Boolean,
      default: false,
    },
    controlAction: {
      type: Boolean,
      default: false,
    },
    autocomplete: {
      type: String,
      default: "",
    },
    validationActivated: {
      type: Boolean,
      default: true,
    },
    showValidationErrors: {
      type: Boolean,
      default: true,
    },
    inCard: {
      type: Boolean,
      default: false,
    },
  },

  emits: ["update:modelValue", "manual-change", "validation-changed"],
  data() {
    return {
      innerValue: "" as string | number,
      isMandatory: false,
      rulesWithoutRequiredRulesIfValidationDisabled: "",
      // Debounced function for manual-change on textareas (500ms delay)
      debouncedManualChange: null as ReturnType<typeof debounce> | null,
      // Flag to emit input-dirty only once per editing session (reset on blur)
      hasTypedSinceBlur: false,
    }
  },
  watch: {
    // innerValue changes are local only - no emit here
    // The parent is notified via blur event
    modelValue(newVal: string | number, _oldValue: string | number): void {
      // Sync innerValue when parent updates modelValue (e.g., on save or external change)
      if (this.innerValue !== newVal) {
        this.innerValue = newVal
      }

      if (newVal != _oldValue) {
        // Use debounce for textarea to avoid triggering validation too frequently
        if (this.$attrs.type === "textarea" && this.debouncedManualChange) {
          this.debouncedManualChange(newVal)
        } else {
          this.$emit("manual-change", newVal)
        }
      }
    },
    rules(): void {
      this.onRulesChanged()
    },
    validationActivated(): void {
      this.onRulesChanged()
    },
  },

  created(): void {
    // Initialize debounced function for textareas (500ms delay for smooth typing)
    this.debouncedManualChange = debounce((val: string | number) => {
      this.$emit("manual-change", val)
    }, 500)

    if (this.modelValue != null) {
      this.innerValue = this.modelValue
    }
    this.onRulesChanged()
  },
  beforeUnmount(): void {
    // Cancel any pending debounced calls
    if (this.debouncedManualChange) {
      this.debouncedManualChange.cancel()
    }
  },
  mounted(): void {
    this.$nextTick(() => {
      this.adjustTextAreaHeight()
    })
  },
  methods: {
    // Notify that user started typing (once per editing session)
    notifyInputDirty(): void {
      if (!this.hasTypedSinceBlur && !this.disabled) {
        this.hasTypedSinceBlur = true
        eventBus.emit("input-dirty")
      }
    },
    // Emit update:modelValue only on blur - this prevents reactivity cascade during typing
    onBlur(event: any, handleBlur: ((e: any) => void) | undefined): void {
      // Reset typing flag so next edit session can signal dirty again
      this.hasTypedSinceBlur = false
      // Notify parent of the final value when user leaves the field
      this.$emit("update:modelValue", this.innerValue)
      // Call vee-validate's blur handler for validation
      if (handleBlur) {
        handleBlur(event)
      }
    },
    updateValue(newValue: string | number): void {
      this.innerValue = newValue
      // Émettre immédiatement pour synchroniser avec le parent
      this.$emit("update:modelValue", newValue)
    },
    adjustTextAreaHeight(): void {
      // Access the input element using the ref
      const field = this.$refs.inputField as any

      // Check if the input element is available and correctly referenced
      if (!field || !field.$el) {
        // Don't warn on component initialization - just return silently
        return
      }
      // Assuming the actual input element is the root element of 'b-input' or within it
      const actualInput = field.$el.querySelector("textarea")

      if (!actualInput || !actualInput.style) {
        // Don't warn if textarea not found - it might be an input field instead
        return
      }

      // Reset field height
      actualInput.style.height = "inherit"

      // Calculate the height
      const computed = window.getComputedStyle(actualInput)
      let height =
        parseInt(computed.getPropertyValue("border-top-width"), 10) +
        parseInt(computed.getPropertyValue("padding-top"), 10) +
        actualInput.scrollHeight +
        parseInt(computed.getPropertyValue("padding-bottom"), 10) +
        parseInt(computed.getPropertyValue("border-bottom-width"), 10)
      if (height < 25) {
        height = 150
      }

      actualInput.style.height = height + "px"
    },

    onRulesChanged(): void {
      // Handle both string and array formats for rules
      if (this.rules) {
        if (Array.isArray(this.rules)) {
          this.isMandatory = this.rules.includes("required")
        } else {
          // String format like "required|email"
          this.isMandatory = this.rules.includes("required")
        }
      } else {
        this.isMandatory = false
      }

      if (this.rules) {
        this.rulesWithoutRequiredRulesIfValidationDisabled = this.rules.toString()
        if (!this.validationActivated) {
          this.rulesWithoutRequiredRulesIfValidationDisabled = filterRulesByValidationStatus(
            this.rules,
            this.validationActivated,
          )
        }
      } else {
        this.rulesWithoutRequiredRulesIfValidationDisabled = ""
      }
    },
  },
})
</script>

<style lang="scss">
@import "@/styles/variables";

.InputWithValidation-field {
  margin-bottom: $input-field-margin-bot;

  .InputWithValidation-control {
    margin-left: 10px;

    span {
      width: 100%;
    }

    .is-static {
      color: $dark;
    }
  }
}

.InputWithValidation-control-container {
  display: flex;

  &.control-right {
    flex-direction: row-reverse;
  }

  .control {
    width: 100%;
  }
}

.auto-expand {
  min-height: 30px; // Minimum height
  overflow-y: hidden; // Hide vertical scrollbar
  resize: none; // Disable manual resize
}

.InputWithValidation-fieldLabel {
  padding-top: 12px;
  display: flex;
  justify-content: space-between;
  width: 100%;

  .mandatory {
    padding-left: 20px;
    color: $dark;
  }
}

#app .InputWithValidation-disabled {
  color: $text-default-color;
}
</style>
